import {
  Pagination,
  Table,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  EmptyPlaceholder,
  Skeleton,
  TableCell,
  TableRow,
  TableBody,
} from '@client/toolkit';
import React, { useState, useEffect } from 'react';
import { SortableTableHeadCell } from './sortable-table-head-cell';
import { useTranslation } from 'react-i18next';

export type SortDirection = 'asc' | 'desc';

export function ExpanableTableWraper<T>({
  loading,
  columns,
  data,
  pageSize,
  renderBody,
  selectable = true,
  onSelected,
  onSort,
  sortColumn,
  sortDirection,
}: {
  loading?: boolean;
  columns: { key: string; label: string; isSortable?: boolean; className?: string }[];
  data: T[];
  pageSize?: number;
  renderBody: (
    sortedData: T[],
    selectedRows: Set<string>,
    expandedRows: Set<string>,
    handleToggleSelect: (id: string) => void,
    handleKeyDownSelect: (e: React.KeyboardEvent, id: string) => void,
    handleToggleExpand: (id: string) => void,
    handleKeyDownExpand: (e: React.KeyboardEvent, id: string) => void,
  ) => React.ReactNode;
  selectable?: boolean;
  onSelected?: (ids: Set<string>) => void;
  onSort?: (col: string, direction: SortDirection) => void;
  sortColumn?: string;
  sortDirection?: SortDirection;
}) {
  const { t } = useTranslation('reporting');
  const { t: tCommon } = useTranslation('common');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string | undefined>(sortColumn);
  const [direction, setSortDirection] = useState<SortDirection | undefined>(sortDirection);
  const [currentPage, setCurrentPage] = useState(0);

  const handleToggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleToggleSelect = (id: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSort = (col: string) => {
    if (sortField === col) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(col);
      setSortDirection('asc');
    }
  };

  const handleKeyDownExpand = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleExpand(id);
    }
  };

  const handleKeyDownSelect = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleSelect(id);
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortField || !direction) {
      return 0;
    }
    let aValue: any = a[sortField as keyof T];
    let bValue: any = b[sortField as keyof T];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  useEffect(() => {
    onSelected?.(selectedRows);
  }, [selectedRows]);

  useEffect(() => {
    if (sortField && direction) {
      onSort?.(sortField, direction);
    }
  }, [sortField, direction]);

  if (data.length === 0 && !loading) {
    return <EmptyPlaceholder className="border-none">{t('noData')}</EmptyPlaceholder>;
  }

  return (
    <>
      <Table className="w-full overflow-hidden rounded-md">
        <TableHead>
          <TableHeadRow>
            {selectable && (
              <TableHeadCell size="medium" className="px-2 py-3">
                <span>{tCommon('select')}</span>
              </TableHeadCell>
            )}
            {columns.map((col) =>
              col.isSortable ? (
                <SortableTableHeadCell
                  className={col.className}
                  id={col.key}
                  key={col.key}
                  label={col.label}
                  handleSort={handleSort}
                  sortColumn={sortField}
                  sortDirection={direction}
                />
              ) : (
                <TableHeadCell id={col.key} key={col.key} className={col.className}>
                  <span dangerouslySetInnerHTML={{ __html: col.label }} />
                </TableHeadCell>
              ),
            )}
          </TableHeadRow>
        </TableHead>
        {loading ? (
          <TableBody>
            <TableRow>
              <TableCell key="select">
                <Skeleton className="h-10 w-full" />
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  <Skeleton className="h-10 w-full" />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        ) : (
          renderBody(
            sortedData,
            selectedRows,
            expandedRows,
            handleToggleSelect,
            handleKeyDownSelect,
            handleToggleExpand,
            handleKeyDownExpand,
          )
        )}
      </Table>
      {pageSize && (
        <Pagination
          totalCount={data.length}
          currentPage={currentPage}
          setPage={setCurrentPage}
          pageSize={pageSize}
          visible={true}
        />
      )}
    </>
  );
}
